import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import CategoryModal from './CategoryModal';
import { toast } from 'react-toastify';
import './Services.css';
import DynamicIcon from '../Common/DynamicIcon';
import ConfirmModal from '../Common/ConfirmModal';

const CategoriesList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get(ENDPOINTS.SERVICES.CATEGORIES);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteModal({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const { id } = deleteModal;
    try {
      await api.delete(`${ENDPOINTS.SERVICES.CATEGORIES}/${id}`);
      toast.success('Category deleted successfully');
      fetchCategories();
      setDeleteModal({ isOpen: false, id: null });
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleCreate = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
  };

  if (loading) {
    return <div className="loading-container"><div className="loader"></div></div>;
  }

  return (
    <div className="categories-list-container">
      <div className="categories-header">
        <h3>Categories</h3>
        <button className="btn btn-primary" onClick={handleCreate}>
          <FaPlus /> Add Category
        </button>
      </div>

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">No categories found</td>
              </tr>
            ) : (
              categories.map((category) => (
                <tr key={category.id}>
                  <td>
                    <div className="category-name-cell">
                      <div className="category-icon-preview">
                        {category.image ? (
                          <img 
                            src={category.image.startsWith('http') ? category.image : `${(api.defaults.baseURL || '').replace('/api', '')}${category.image}`} 
                            alt={category.name} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              // Only fallback to icon if the image really doesn't exist AND we haven't already marked it broken
                              if (!brokenImages[category.id]) {
                                setBrokenImages(prev => ({ ...prev, [category.id]: true }));
                              }
                            }}
                          />
                        ) : (
                          <DynamicIcon name={category.icon || 'Package'} size={14} />
                        )}
                      </div>
                      {category.name}
                    </div>
                  </td>
                  <td>{category.description}</td>
                  <td>{category.slug}</td>
                  <td>
                    {category.isActive ? (
                      <span className="badge badge-success">Active</span>
                    ) : (
                      <span className="badge badge-danger">Inactive</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon edit"
                        onClick={() => handleEdit(category)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDelete(category.id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CategoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        category={selectedCategory}
        refreshCategories={fetchCategories}
      />

      <ConfirmModal 
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Category?"
        message="Are you sure you want to delete this category? This will permanently remove the category and all associated services. This action cannot be undone."
        confirmText="Permanently Delete"
      />
    </div>
  );
};

export default CategoriesList;
